import {
  HISTORY_LEN,
  levelLabelRu,
  lossToLevel,
  rttToLevel,
  sparkHeightPct,
  SPARK_LOSS_MAX_PCT,
  SPARK_RTT_MAX_MS,
} from "./labels";
import styles from "./LegSpark.module.scss";

export interface SparkSample {
  loss_pct?: number | null;
  lossPercentRecent?: number | null;
  rtt_ms?: number | null;
  rttMs?: number | null;
}

export interface LegSparkProps {
  kind: "loss" | "rtt";
  samples?: SparkSample[];
  label?: string;
}

/**
 * Однорядный spark одной метрики (Loss или RTT) для встраивания в StatusChip.
 * Высота = здоровье (выше = лучше), цвет = порог. Слева старее → справа новее.
 */
export function LegSpark({ kind, samples, label }: LegSparkProps) {
  const isLoss = kind === "loss";
  const toLevel = isLoss ? lossToLevel : rttToLevel;
  const max = isLoss ? SPARK_LOSS_MAX_PCT : SPARK_RTT_MAX_MS;
  const title = label || (isLoss ? "Loss history" : "RTT history");

  const items: (SparkSample | null)[] = Array.isArray(samples) ? samples.slice(-HISTORY_LEN) : [];
  while (items.length < HISTORY_LEN) {
    items.unshift(null);
  }

  const tipLines = [
    `${title} (слева старее → справа новее)`,
    "Высота = здоровье (выше лучше), цвет = порог. Не ABR-профиль.",
    ...items.map((s, i) => {
      if (!s) return `t-${HISTORY_LEN - 1 - i}: —`;
      const raw = isLoss ? (s.loss_pct ?? s.lossPercentRecent) : (s.rtt_ms ?? s.rttMs);
      const text =
        raw != null && Number.isFinite(Number(raw))
          ? isLoss
            ? `${Number(raw).toFixed(1)}%`
            : `${Math.round(Number(raw))} ms`
          : "—";
      const newest = i === items.length - 1 ? " · сейчас" : "";
      return `t-${HISTORY_LEN - 1 - i}: ${text} (${levelLabelRu(toLevel(raw))})${newest}`;
    }),
  ];

  return (
    <span className={styles.root} title={tipLines.join("\n")} aria-label={title} role="img">
      <span className={styles.track}>
        {items.map((s, i) => {
          const raw = s
            ? isLoss
              ? (s.loss_pct ?? s.lossPercentRecent)
              : (s.rtt_ms ?? s.rttMs)
            : null;
          const height = sparkHeightPct(raw, max);
          const level = height == null ? "empty" : toLevel(raw);
          const newest = height != null && i === items.length - 1;
          return (
            <span
              key={`${kind}-${i}`}
              className={[styles.cell, styles[level] ?? "", newest ? styles.newest : ""]
                .filter(Boolean)
                .join(" ")}
              style={height != null ? { height: `${height}%` } : undefined}
            />
          );
        })}
      </span>
    </span>
  );
}
